import { useContext, useEffect, useMemo, useState } from 'react';
import { ReactRelayContext } from 'react-relay';
import { createOperationDescriptor, fetchQuery, getRequest } from 'relay-runtime';
import type { IEnvironment, OperationDescriptor } from 'relay-runtime';

import { expVal } from '@confluence/feature-experiments';

import { CONTENT_STATUS_ARCHIVED } from './archivingConstants.ts';
import ArchivedPageBannerV2ArchivedPageBannerContainerQuery, {
	type ArchivedPageBannerV2ArchivedPageBannerContainerQuery$data,
} from './__generated__/ArchivedPageBannerV2ArchivedPageBannerContainerQuery.graphql.ts';

type StatusQueryTypes = {
	response: ArchivedPageBannerV2ArchivedPageBannerContainerQuery$data;
	variables: { contentId: string };
};

/** The id of the node if it is archived, null if it isn't, undefined if the status isn't in the store. */
const readArchivedIdFromStore = (
	environment: IEnvironment,
	operation: OperationDescriptor,
): string | null | undefined => {
	if (environment.check(operation).status !== 'available') {
		return undefined;
	}
	const data = environment.lookup(operation.fragment).data as
		| ArchivedPageBannerV2ArchivedPageBannerContainerQuery$data
		| null
		| undefined;
	const node = data?.content?.nodes?.[0];
	if (!node) {
		return undefined;
	}
	return node.status === CONTENT_STATUS_ARCHIVED ? (node.id ?? null) : null;
};

/**
 * Whether the given content is archived. Reuses the archived banner's status query, which the
 * view page already preloads.
 *
 * When the status is already in the Relay store it is read synchronously, so the very first render
 * is correct (like the overview, which knows its page up front) and consumers can load once with
 * the right id. Otherwise it fetches without suspending and returns false until the status is known.
 */
export const useIsContentArchived = (contentId: string | null | undefined): boolean => {
	// Read the environment from context instead of `useRelayEnvironment`, which throws without a provider.
	// Callers such as the move dialog's tree can render outside one (e.g. Apollo-only tests); with no
	// environment there is nothing to read or fetch, so the hook reports "not archived".
	const environment = useContext(ReactRelayContext)?.environment;
	const [fetchedArchivedId, setFetchedArchivedId] = useState<string | null>(null);
	// Bumped when the stored status changes (e.g. the page is restored) so the sync read stays fresh.
	const [storeVersion, setStoreVersion] = useState(0);

	// Gated by `confluence_archive_folder`: with it off there is no operation, so nothing is read or
	// fetched and every consumer of this hook sees `false` (the existing behavior).
	const operation = useMemo(
		() =>
			contentId && environment && expVal('confluence_archive_folder', 'isEnabled', false)
				? createOperationDescriptor(
						getRequest(ArchivedPageBannerV2ArchivedPageBannerContainerQuery),
						{
							contentId,
						},
					)
				: null,
		[contentId, environment],
	);

	const storedArchivedId = useMemo(
		() => (operation && environment ? readArchivedIdFromStore(environment, operation) : undefined),
		// eslint-disable-next-line react-hooks/exhaustive-deps -- `storeVersion` re-reads the store after it changes
		[environment, operation, storeVersion],
	);

	useEffect(() => {
		if (!contentId || !operation || !environment) {
			setFetchedArchivedId(null);
			return;
		}

		const fetchSubscription = fetchQuery<StatusQueryTypes>(
			environment,
			ArchivedPageBannerV2ArchivedPageBannerContainerQuery,
			{ contentId },
			{ fetchPolicy: 'store-or-network' },
		).subscribe({
			next: (data) => {
				const node = data?.content?.nodes?.[0];
				setFetchedArchivedId(node?.status === CONTENT_STATUS_ARCHIVED ? (node.id ?? null) : null);
			},
			error: () => setFetchedArchivedId(null),
		});

		const storeDisposable = environment.subscribe(environment.lookup(operation.fragment), () =>
			setStoreVersion((version) => version + 1),
		);

		return () => {
			fetchSubscription.unsubscribe();
			storeDisposable.dispose();
		};
	}, [environment, operation, contentId]);

	if (!contentId) {
		return false;
	}
	const archivedId = storedArchivedId !== undefined ? storedArchivedId : fetchedArchivedId;
	return archivedId === contentId;
};
